import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * DeliveryMethodSelector
 *
 * Shows delivery options at checkout when Chowdeck is available.
 * Allows customer to choose between standard shipping and Chowdeck express delivery.
 */
function DeliveryMethodSelector({
  storeSlug,
  deliveryState,
  deliveryAddress,
  deliveryCity,
  standardShippingCost,
  standardShippingMethod,
  standardEstimate,
  currency,
  onDeliveryMethodChange,
  disabled
}) {
  const [loading, setLoading] = useState(false);
  const [chowdeckAvailable, setChowdeckAvailable] = useState(false);
  const [chowdeckFee, setChowdeckFee] = useState(null);
  const [chowdeckFeeId, setChowdeckFeeId] = useState(null);
  const [chowdeckQuoteToken, setChowdeckQuoteToken] = useState(null);
  const [chowdeckEstimate, setChowdeckEstimate] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('STATIC'); // STATIC or CHOWDECK
  const [error, setError] = useState(null);

  // Check Chowdeck availability when delivery state changes
  useEffect(() => {
    if (!deliveryState || !storeSlug) {
      setChowdeckAvailable(false);
      return;
    }

    const checkChowdeckAvailability = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.post('/api/chowdeck/calculate-fee', {
          storeSlug,
          deliveryState,
          deliveryAddress,
          deliveryCity
        });

        if (response.data.success && response.data.available) {
          setChowdeckAvailable(true);
          setChowdeckFee(response.data.deliveryFee);
          setChowdeckFeeId(response.data.feeId);
          setChowdeckQuoteToken(response.data.quoteToken);
          setChowdeckEstimate(response.data.estimatedTime);
        } else {
          setChowdeckAvailable(false);
          setChowdeckFee(null);
          setChowdeckFeeId(null);
          setChowdeckQuoteToken(null);
          setChowdeckEstimate(null);
        }
      } catch (err) {
        console.error('[DeliveryMethodSelector] Chowdeck check failed:', err);
        setChowdeckAvailable(false);
        setError(err.response?.data?.error || 'Failed to check delivery options');
      } finally {
        setLoading(false);
      }
    };

    checkChowdeckAvailability();
  }, [storeSlug, deliveryState, deliveryAddress, deliveryCity]);

  // Notify parent when selection changes
  useEffect(() => {
    if (selectedMethod === 'CHOWDECK' && chowdeckAvailable) {
      onDeliveryMethodChange({
        method: 'CHOWDECK',
        fee: chowdeckFee,
        feeId: chowdeckFeeId,
        quoteToken: chowdeckQuoteToken,
        estimate: chowdeckEstimate
      });
    } else {
      onDeliveryMethodChange({
        method: 'STATIC',
        fee: standardShippingCost,
        feeId: null,
        estimate: standardEstimate
      });
    }
  }, [selectedMethod, chowdeckAvailable, chowdeckFee, chowdeckFeeId, chowdeckQuoteToken, chowdeckEstimate, standardShippingCost, standardEstimate, onDeliveryMethodChange]);

  const formatCurrency = (amount) => {
    if (currency?.format) {
      return currency.format(amount);
    }
    return `₦${amount?.toLocaleString() || 0}`;
  };

  // Don't show selector if Chowdeck not available or loading
  if (loading) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        marginTop: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            border: '2px solid #d1d5db',
            borderTop: '2px solid #111827',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            Checking delivery options...
          </span>
        </div>
      </div>
    );
  }

  // If Chowdeck not available, don't show selector (standard shipping will be used)
  if (!chowdeckAvailable) {
    return null;
  }

  return (
    <div style={{
      marginTop: '20px',
      padding: '16px',
      backgroundColor: '#f0f9ff',
      borderRadius: '12px',
      border: '1px solid #bae6fd'
    }}>
      <h4 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#0c4a6e',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>🚀</span>
        Choose Delivery Method
      </h4>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Standard Shipping Option */}
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '16px',
            borderRadius: '8px',
            border: selectedMethod === 'STATIC' ? '2px solid #0ea5e9' : '2px solid #e5e7eb',
            backgroundColor: selectedMethod === 'STATIC' ? '#e0f2fe' : 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <input
            type="radio"
            name="deliveryMethod"
            value="STATIC"
            checked={selectedMethod === 'STATIC'}
            onChange={() => setSelectedMethod('STATIC')}
            disabled={disabled}
            style={{ marginTop: '3px' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <span style={{
                  fontWeight: '600',
                  color: '#111827',
                  fontSize: '15px'
                }}>
                  📦 {standardShippingMethod || 'Standard Delivery'}
                </span>
                <p style={{
                  margin: '4px 0 0 0',
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  {standardEstimate || '3-5 business days'}
                </p>
              </div>
              <span style={{
                fontWeight: '600',
                color: '#111827',
                fontSize: '15px'
              }}>
                {formatCurrency(standardShippingCost || 0)}
              </span>
            </div>
          </div>
        </label>

        {/* Chowdeck Express Option */}
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '16px',
            borderRadius: '8px',
            border: selectedMethod === 'CHOWDECK' ? '2px solid #22c55e' : '2px solid #e5e7eb',
            backgroundColor: selectedMethod === 'CHOWDECK' ? '#dcfce7' : 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <input
            type="radio"
            name="deliveryMethod"
            value="CHOWDECK"
            checked={selectedMethod === 'CHOWDECK'}
            onChange={() => setSelectedMethod('CHOWDECK')}
            disabled={disabled}
            style={{ marginTop: '3px' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <span style={{
                  fontWeight: '600',
                  color: '#111827',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ⚡ Chowdeck Express
                  <span style={{
                    padding: '2px 8px',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '600',
                    borderRadius: '10px'
                  }}>
                    FAST
                  </span>
                </span>
                <p style={{
                  margin: '4px 0 0 0',
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  {chowdeckEstimate || '30-60 mins'} • Same-day delivery
                </p>
              </div>
              <span style={{
                fontWeight: '600',
                color: '#22c55e',
                fontSize: '15px'
              }}>
                {formatCurrency(chowdeckFee)}
              </span>
            </div>
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '12px',
              color: '#047857',
              backgroundColor: '#bbf7d0',
              padding: '6px 10px',
              borderRadius: '6px',
              display: 'inline-block'
            }}>
              Delivered by Chowdeck rider • Real-time tracking
            </p>
          </div>
        </label>
      </div>

      {error && (
        <p style={{
          marginTop: '8px',
          fontSize: '12px',
          color: '#dc2626'
        }}>
          {error}
        </p>
      )}
    </div>
  );
}

export default DeliveryMethodSelector;
